import { Autocomplete, Chip, styled, TextField } from '@mui/material';
import { clone, cloneDeep, concat, filter, find, findIndex, map, uniqBy } from 'lodash';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useDebounce from '../../hooks/useDebounce';
import { FuelPortalService } from '../../services/fuel-portal/fuel-portal-service';
import { applicationDataSelector } from '../../store/slices/application-data';
import useAppUtils from '../../utils/appUtils';
import itemUtils from '../../utils/itemUtils';
import FuelConfirmDialog from '../fuel-confirm-dialog';

interface IProps {
  id: string;
  tags: Array<string>;
  inputTagWidth: number;
  onChange?: (tags: []) => void;
  labelText?: string;
  excludedTags?: string[];
  disabled?: boolean;
  /** When provided, tag suggestions are fetched from all listed OU IDs and merged (doc_counts summed). Falls back to the current OU when empty. */
  ouIds?: string[];
}
const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-inputRoot': {
    //   color: "purple",
    // This matches the specificity of the default styles at https://github.com/mui-org/material-ui/blob/v4.11.3/packages/material-ui-lab/src/Autocomplete/Autocomplete.js#L90
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
      // Default left padding is 6px
      // paddingLeft: 26
      // height: '16px',
      // padding: '2px'
    },
    //   "& .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "green"
    //   },
    //   "&:hover .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "red"
    //   },
    //   "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    //     borderColor: "purple"
    //   }

    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-tag': {
      // Default left padding is 6px
      // paddingLeft: 26
      margin: 0,
      marginRight: '3px',
      marginBottom: '1px',
      maxWidth: '175px',
    },
  },
  '& .MuiAutocomplete-root': {
    '& .MuiFormControl-root': {
      '&[class*="MuiInputLabel-root"] .MuiInputLabel-formControl': {
        top: '5px',
      },
    },
  },
});

const FuelTags = ({
  id,
  tags,
  inputTagWidth,
  onChange,
  labelText = 'Select Tags',
  excludedTags = [],
  disabled = false,
  ouIds = [],
}: IProps) => {
  const { ouData } = useSelector(applicationDataSelector);
  const [inputTag, setInputTag] = useState<string>('');
  const [openTagList, setOpenTagList] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [value, setValue] = useState(tags);
  const [open, setOpen] = useState<boolean>(false);
  const [tag, setTag] = useState<string>('');
  const debouncedValue = useDebounce<string>(tag, 500);
  const [oldTag, setOldTag] = useState<string>('');
  const { alert } = useAppUtils();

  const handleTagInput = async (event: React.ChangeEvent<{}>, newTagInput: string) => {
    // event.preventDefault();
    setTag(event.target.value);
    const options = newTagInput.split(/[,]+/);

    const fieldValue = value
      .concat(options)
      .map((x) => x.trim())
      .filter((x) => x);

    if (options.length > 1) {
      handleChange(event, fieldValue);
    } else {
      setInputTag(newTagInput);
    }
  };

  const handleChange = (event: React.ChangeEvent<{}>, tags: string[]) => {
    const freshTags = refreshTags(tags);
    setValue(freshTags);
    event.persist();
    setIsUpdating(false);
    onChange(freshTags);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // console.log('event', event.key);
    switch (event.key) {
      // 2022-07-13 aalt prevent all commas and backslashes in tags names
      case ',':
      case '\\': {
        event.preventDefault();
        alert('Commas and backslashes not allowed in tag names.', 'warning');
        break;
      }
      case 'Tab':
      case 'Enter': {
        if (inputTag.length > 0) {
          handleChange(event, value.concat([inputTag]));
          handleTagInput(event, '');
        }
        break;
      }
      default:
    }
  };
  const refreshTags = (tags: string[]) => {
    setIsUpdating(true);

    const clonedTags = clone(tags);
    const tagsCopy = itemUtils.cleanupTags(clonedTags);

    // let tagsCopy = filter(
    //   uniq(
    //     map(clone(tags), (t) => {
    //       return itemUtils.isMetaTag(t) ? itemUtils.getSafeMetaTagString(t) : t.trim().toLowerCase();
    //     })
    //   ),
    //   size
    // );

    setTimeout(() => {
      setIsUpdating(false);
    }, 30);
    return tagsCopy;
  };

  const handleTagDoubleClick = (event, tag: string) => {
    switch (event.detail) {
      case 1:
        // console.log('click');
        break;
      case 2:
        // console.log('double click');
        openEditTagDialog(tag);
        break;
      case 3:
        // console.log('triple click');
        break;
    }
  };

  const openEditTagDialog = (tag: string) => {
    // console.log('Edit tag value', tag);
    setTag(tag);
    setOldTag(tag);
    setOpen(true);
  };

  const editTag = (isOk) => {
    if (!isOk) {
      return;
    }
    let newValue = tag;
    if (itemUtils.isMetaTag(newValue)) {
      newValue = itemUtils.getSafeMetaTagString(newValue);
    } else {
      newValue = itemUtils.getSafeTagString(newValue);
    }

    const index = findIndex(value, (t) => t == oldTag);
    if (index > -1) {
      const tempTags = clone(value);
      tempTags.splice(index, 1, newValue);
      updateTagsWithValue(tempTags);
    }
  };
  const updateTagsWithValue = (items: string[]) => {
    if (!isUpdating) {
      setIsUpdating(true);
      const oldVal = clone(value);
      const freshTags = refreshTags(items);
      setValue(freshTags);
      onChange(freshTags);
    }
  };
  const handleTagChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTag((event.target as HTMLInputElement).value);
  };

  const handlePaste = (e) => {
    // console.log('onPaste', e);
  };

  const getSearchResults = (searchText: string) => {
    const effectiveOuIds = ouIds.length > 0 ? ouIds : (ouData?.id ? [ouData.id] : []);
    if (searchText.length === 0 || effectiveOuIds.length === 0) {
      return;
    }
    setSearchResults([]);
    setSuggestedTags([]);

    Promise.all(effectiveOuIds.map((ouId) => FuelPortalService.searchTags(ouId, searchText)))
      .then((perOuResults) => {
        // Flatten and translate to { key, doc_count } format, then sum doc_counts across OUs
        const countMap = new Map<string, number>();
        perOuResults.forEach((tags) => {
          tags.forEach((tag) => {
            if (tag.item1) {
              countMap.set(tag.item1, (countMap.get(tag.item1) ?? 0) + tag.item2);
            }
          });
        });

        let adjustedResults = Array.from(countMap.entries()).map(([key, doc_count]) => ({ key, doc_count }));
        adjustedResults = filter(adjustedResults, (i) => {
          return !value.includes(i.key) && !excludedTags.includes(i.key);
        });
        setSearchResults(adjustedResults);
        setSuggestedTags(
          map(adjustedResults, (p) => {
            return p.key;
          })
        );
      })
      .catch((err) => {
        console.error('Unable to get tags due to', err);
      });
  };

  const getTagLabel = (tag: string) => {
    const tagObject = find(searchResults, { key: tag });
    const tagCount = tagObject ? tagObject.doc_count : null;
    return tagCount ? `${tag} (${tagCount})` : tag;
  };

  const onDeleteTag = (event, option, index) => {
    // console.log('onDeleteTag', { index, event, option, value });
    const newValues = cloneDeep(value);
    newValues.splice(index, 1);
    setValue(newValues);
    onChange(newValues);
  };

  const renderTags = (value: string[], getTagProps) => {
    // console.log('renderTags', { value });
    return value.map((option: string, index: number) => {
      const otherOptions = getTagProps({ index });
      // console.log('renderTags', { otherOptions });
      if (otherOptions) {
        delete otherOptions.onDelete;
        delete otherOptions.key; // solve React Warning: A props object containing a "key" prop is being spread into JSX:
        if (otherOptions.className) {
          otherOptions.className += ' tag-chip';
          if (itemUtils.isMetaTag(option)) otherOptions.className += ' meta';
          if (labelText === 'Excluding') otherOptions.className += ' exclude';
        }
      }
      return (
        <Chip
          key={index}
          clickable
          size="small"
          color="primary"
          onClick={(e) => handleTagDoubleClick(e, option)}
          title={option}
          label={option}
          sx={{ borderRadius: 0 }}
          onDelete={(e) => onDeleteTag(e, option, index)}
          {...otherOptions}
        />
      );
    });
  };

  // Stable string key so the effect re-runs whenever the OU selection changes,
  // even if the tag search text hasn't changed.
  const ouIdsKey = ouIds.join(',');

  useEffect(() => {
    // Do fetch here...
    // Triggers when "debouncedValue" changes
    // console.log('call', debouncedValue);
    if (debouncedValue && debouncedValue.length > 2) {
      getSearchResults(debouncedValue);
    } else {
      setSearchResults([]);
      setSuggestedTags([]);
    }
  }, [debouncedValue, ouIdsKey]);

  useEffect(() => {
    // console.log(tags, value);
    setValue(tags);
  }, [tags]);

  return (
    <div className="fuel-tags">
      <StyledAutocomplete
        id={id}
        disableClearable
        options={suggestedTags}
        getOptionLabel={(option) => getTagLabel(option)}
        value={value || []}
        inputValue={inputTag !== null ? inputTag : ''}
        multiple
        freeSolo
        size="small"
        limitTags={2}
        onChange={(event, newValue) => handleChange(event, newValue)}
        onInputChange={handleTagInput}
        renderTags={(value: string[], getTagProps) => renderTags(value, getTagProps)}
        sx={{ width: inputTagWidth }}
        renderInput={(params) => {
          params.inputProps.onKeyDown = handleKeyDown;
          return <TextField fullWidth placeholder="Type to find tags..." {...params} label={labelText} />;
        }}
        disabled={disabled}
      />
      <FuelConfirmDialog title="" open={open} setOpen={setOpen} onConfirm={(e) => editTag(e)}>
        <TextField
          variant="outlined"
          label="Edit Tag"
          value={tag}
          fullWidth
          onChange={handleTagChange}
          onPaste={(e) => handlePaste}
        />
      </FuelConfirmDialog>
    </div>
  );
};

export default FuelTags;
